import Dexie from "dexie";
import moment from "moment";
import { notify, nameFormatting, manageErrorLogs } from "./commonFunction";
import { reqToStartSyncProcess } from "../store/slice/offlineSlice";

export const DB = new Dexie("fogapp");
DB.version(1)
  .stores({
    userData: "++id",
    errorLogs: "++id",
    mediaFiles: "++id, file",
    systemLogs: "++id",
    lastAPICallTime: "++id",
    takedDetails: "++id",
    addedTakeDetails: "++id",
    restaurantDetails: "++id",
    addedRestaurantDetails: "++id",
    restaurantDetailsReports: "++id",
  })
  .upgrade(async (tx) => {});
DB.version(2)
  .stores({
    userData: "++id",
    errorLogs: "++id",
    mediaFiles: "++id, file",
    systemLogs: "++id",
    lastAPICallTime: "++id",
    takedDetails: "++id",
    addedTakeDetails: "++id",
    restaurantDetails: "++id",
    addedRestaurantDetails: "++id",
    restaurantDetailsReports: "++id",
  })
  .upgrade(async (tx) => {});
DB.version(3)
  .stores({
    userData: "++id",
    errorLogs: "++id",
    mediaFiles: "++id, file",
    systemLogs: "++id",
    lastAPICallTime: "++id",
    restaurantDetails: "++id",
    addedRestaurantDetails: "++id",
    restaurantDetailsReports: "++id",
  })
  .upgrade(async (tx) => {});
DB.version(4).stores({
  userData: "++id",
  errorLogs: "++id",
  mediaFiles: "++id, file",
  systemLogs: "++id",
  lastAPICallTime: "++id",
  restaurantDetails: "++id",
  addedRestaurantDetails: "++id",
  restaurantDetailsReports: "++id",
  addedRestaurantTaskNotes: "++id",
});
DB.version(5)
  .stores({
    userData: "++id",
    errorLogs: "++id",
    mediaFiles: "++id, file",
    systemLogs: "++id",
    lastAPICallTime: "++id",
    restaurantDetails: "++id",
    restaurantDetailsReports: "++id",
    addedRestaurantDetails: "++id",
    govermentMarketDetails: "++id",
    govermentMarketReports: "++id",
    addedRestaurantTaskNotes: "++id",
  })
  .upgrade(async (tx) => {});

let dbInitializationPromise = null;
const initializeDatabase = async () => {
  if (dbInitializationPromise) {
    return dbInitializationPromise;
  }

  dbInitializationPromise = (async () => {
    try {
      if (!DB.isOpen()) {
        await DB.open();
      }
      await DB.table("userData").limit(1).toArray();
      return { success: true };
    } catch (error) {
      dbInitializationPromise = null;
      try {
        if (!DB.isOpen()) {
          await DB.open();
        }
        await DB.table("userData").limit(1).toArray();
        return { success: true };
      } catch (retryError) {
        dbInitializationPromise = null;
        manageErrorLogs({
          ...retryError,
          message: `Database initialization retry failed: ${retryError?.message}`,
          date: new Date(),
        });
        return { success: false, error: retryError };
      }
    }
  })();
  return dbInitializationPromise;
};

// Auto-initialize database
if (typeof window !== "undefined") {
  initializeDatabase().catch((error) => {
    manageErrorLogs({
      ...error,
      message: `Failed to auto-initialize database: ${error?.message}`,
      date: new Date(),
    });
  });
}

DB.on("close", async () => {
  try {
    await reconnectDB();
  } catch (error) {
    manageErrorLogs(error);
  }
});

DB.on("blocked", async () => {
  try {
    await reconnectDB();
  } catch (error) {
    manageErrorLogs(error);
  }
});

const isRecoverableConnectionError = (error) => {
  const msg = error?.message?.toLowerCase() || "";
  return (
    msg.includes("abort") ||
    msg.includes("database has been closed") ||
    msg.includes("connection to indexed database server lost")
  );
};

const reconnectDB = async (retries = 5) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      if (DB.isOpen()) return true;
      await DB.open();
    } catch (error) {
      if (attempt === retries) {
        manageErrorLogs({
          message: `Reconnect failed after ${retries} attempts: ${error?.message}`,
          attempt,
          error: error?.stack || error?.message,
        });
        throw error;
      }

      const recoverable = isRecoverableConnectionError(error);
      const delay = recoverable ? 1000 * attempt : 200;
      await new Promise((r) => setTimeout(r, delay));

      if (recoverable && DB.isOpen()) {
        try {
          DB.close();
        } catch (closeError) {
          manageErrorLogs(
            `DB close failed during retry ${attempt}: ${closeError?.message}`
          );
        }
      }
    }
  }
  throw new Error("DB connection failed after all retries");
};

const executeTransaction = async (mode, tableNames, action, retries = 5) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      await reconnectDB();
      const result = await DB.transaction(mode, tableNames, async (tx) => {
        try {
          return await action(tx);
        } catch (innerError) {
          if (isRecoverableConnectionError(innerError)) {
            throw new Error("Database closed during transaction");
          }
          throw innerError;
        }
      });
      return result;
    } catch (error) {
      if (attempt === retries) {
        manageErrorLogs({
          message: `Safe transaction (${mode}) failed after ${retries} attempts: ${error?.message}`,
          mode,
          attempt,
          error: error?.stack || error?.message,
        });
        throw error;
      }

      if (isRecoverableConnectionError(error)) {
        await new Promise((r) => setTimeout(r, 1000 * attempt));
        if (DB.isOpen()) {
          try {
            DB.close();
          } catch (closeError) {}
        }
        continue;
      } else {
        await new Promise((r) => setTimeout(r, 200 * attempt));
      }
    }
  }
  throw new Error("Transaction failed after all retries");
};

export const safeTransaction = async (tableNames, action, retries = 5) => {
  return executeTransaction("rw", tableNames, action, retries);
};

export const safeReadTransaction = async (tableNames, action, retries = 5) => {
  return executeTransaction("r", tableNames, action, retries);
};

export const isTableExists = (table) => {
  if (!DB.tables.some((t) => t.name === table)) {
    throw new Error(`Table '${table}' does not exist.`);
  }
  return true;
};

export const isFieldIndexed = (table, field) => {
  const tableSchema = DB.tables.find((t) => t.name === table).schema;
  return (
    tableSchema.indexes.some((index) => index.name === field) ||
    tableSchema.primKey.name === field
  );
};

export const checkTablesInDB = (tables) => {
  try {
    const tableArr = Array.isArray(tables) ? tables : [tables];
    tableArr.forEach(isTableExists);
  } catch (error) {
    throw error;
  }
};

export const clearTables = async (tx, tables) => {
  try {
    const tableArr = Array.isArray(tables) ? tables : [tables];
    for (const name of tableArr) {
      await tx.table(name).clear();
    }
  } catch (error) {
    throw error;
  }
};

export const clearTablesWithTran = async (tables) => {
  try {
    const tableArr = Array.isArray(tables) ? tables : [tables];
    await safeTransaction(tableArr, async (tx) => {
      for (const name of tableArr) {
        await tx.table(name).clear();
      }
    });
  } catch (error) {
    manageErrorLogs(error);
    return null;
  }
};

export const insertDataInDB = async (table, data, chunkSize = 50) => {
  try {
    const chunks = [];
    for (let i = 0; i < data?.length; i += chunkSize) {
      chunks.push(data.slice(i, i + chunkSize));
    }
    let results = [];
    for (const chunk of chunks) {
      results.push(await table.bulkPut(chunk));
    }
  } catch (error) {
    throw error;
  }
};

export const getAllEntriesFromDB = async (table) => {
  try {
    if (!table) {
      throw new Error(`Invalid or missing data!`);
    }
    isTableExists(table);
    let entries = [];
    await safeTransaction([table], async (tx) => {
      entries = await tx.table(table).toArray();
    });
    return entries;
  } catch (error) {
    manageErrorLogs(error);
    return [];
  }
};

export const getMultipleEntriesFromDB = async (tables) => {
  try {
    if (!Array.isArray(tables) || tables.length === 0) {
      throw new Error(`Invalid or missing table names!`);
    }
    tables.forEach(isTableExists);
    let results = {};
    await safeTransaction(tables, async (tx) => {
      for (const table of tables) {
        const data = await tx.table(table).toArray();
        if (!data) {
          throw new Error(`No data found in table: ${table}`);
        }
        results[table] = data;
      }
    });
    return results;
  } catch (error) {
    throw error;
  }
};

export const getEntryFromDB = async (table, id) => {
  try {
    if (!table || !id) {
      throw new Error(`Invalid or missing data!`);
    }
    isTableExists(table);

    let entry = null;
    await safeTransaction([table], async (tx) => {
      entry = await tx.table(table).get(id);
    });
    return entry;
  } catch (error) {
    manageErrorLogs(error);
    return null;
  }
};

export const getEntryByFieldFromDB = async (table, fields, values) => {
  try {
    if (!table || !fields || !values) {
      throw new Error(`Invalid or missing data!`);
    }
    isTableExists(table);

    let entries = [];
    if (typeof fields === "string" && typeof values !== "object") {
      if (!isFieldIndexed(table, fields)) {
        throw new Error(`Field '${fields}' in '${table}' is not indexed.`);
      }
      await safeTransaction(table, async (tx) => {
        entries = await tx.table(table).where(fields).equals(values).toArray();
      });
    } else if (Array.isArray(fields) && Array.isArray(values)) {
      if (fields.length !== values.length) {
        throw new Error(`Fields and values arrays must have the same length`);
      }
      for (const field of fields) {
        if (!isFieldIndexed(table, field)) {
          throw new Error(`Field '${field}' in '${table}' is not indexed.`);
        }
      }
      await safeTransaction(table, async (tx) => {
        let query = tx.table(table);
        for (let i = 0; i < fields.length; i++) {
          query = query.and((entry) => entry[fields[i]] === values[i]);
        }
        entries = await query.toArray();
      });
    } else {
      throw new Error(`Invalid data types for fields and values!`);
    }
    return entries;
  } catch (error) {
    manageErrorLogs(error);
    return [];
  }
};

export const insertEntryInDB = async (table, newData) => {
  try {
    if (!table || !newData) {
      throw new Error(`Invalid or missing data!`);
    }
    isTableExists(table);
    let id = "";
    await safeTransaction([table], async (tx) => {
      id = await tx.table(table).add(newData);
    });
    return id;
  } catch (error) {
    manageErrorLogs(error);
    return null;
  }
};

export const updateEntryInDB = async (table, field, value, newData) => {
  try {
    if (!table || !field || !value || !newData) {
      throw new Error(`Invalid or missing data!`);
    }
    isTableExists(table);
    if (!isFieldIndexed(table, field)) {
      throw new Error(`Field '${field}' in '${table}' is not indexed.`);
    }

    let id = "";
    await safeTransaction([table], async (tx) => {
      const isExist = await getEntryByFieldFromDB(table, field, value);
      if (isExist?.length > 0) {
        await tx.table(table).update(isExist[0]?.id, newData);
        id = isExist[0]?.id;
      } else {
        id = await tx.table(table).add(newData);
      }
    });
    return id;
  } catch (error) {
    manageErrorLogs(error);
    return false;
  }
};

export const reqToImportTableData = async (file, dispatch) => {
  try {
    dispatch(reqToStartSyncProcess(true));
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const importData = JSON.parse(event.target.result);
        const tableNames = Object.keys(importData);

        await safeTransaction(tableNames, async (tx) => {
          for (const [tableName, records] of Object.entries(importData)) {
            const table = tx.table(tableName);
            if (table) {
              await table.clear();
              await table.bulkAdd(records);
            } else {
              notify(
                `Table "${tableName}" does not exist in the database.`,
                "warning"
              );
            }
          }
        });
        dispatch(reqToStartSyncProcess(false));
        notify("Database imported successfully!", "success");
      } catch (importError) {
        manageErrorLogs(importError);
        dispatch(reqToStartSyncProcess(false));
        notify("Failed to import database!", "error");
      }
    };
    reader.onerror = () => {
      dispatch(reqToStartSyncProcess(false));
      notify("Failed to read the file!", "error");
    };
    reader.readAsText(file);
  } catch (error) {
    manageErrorLogs(error);
    dispatch(reqToStartSyncProcess(false));
    notify("Failed to import database!", "error");
  }
};

export const reqToExportTableData = async (tables, dispatch) => {
  try {
    dispatch(reqToStartSyncProcess(true));
    const userData = await getAllEntriesFromDB("userData");
    const newUserData = userData?.length > 0 ? userData[0] : "";

    const data = await safeReadTransaction(tables, async (tx) => {
      const exportData = {};
      for (const tableName of tables) {
        const table = tx.table(tableName);
        exportData[tableName] = await table.toArray();
      }
      return exportData;
    });

    const blob = new Blob([JSON.stringify(data)], {
      type: "application/json",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${
      newUserData
        ? `${nameFormatting(newUserData)}_${moment().format("YYYY-MM-DD")}_`
        : `${moment().format("YYYY-MM-DD")}_`
    }database.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    notify("Database exported successfully!", "success");
    setTimeout(() => {
      dispatch(reqToStartSyncProcess(false));
    }, 500);
  } catch (error) {
    manageErrorLogs(error);
    dispatch(reqToStartSyncProcess(false));
    notify("Failed to exporting database!", "error");
  }
};
